import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";

// API Services
import {
  cancelClosingCashier,
  createCashierPaymentMethodTransfer,
  createCashierTransaction,
  deleteCashierPaymentMethodTransfer,
  deleteCashierTransaction,
  endCashier,
  getCashierReportOrdersPaymentMethods,
  getCashierReportUnfinishedOrders,
  getCashierTransactions,
  getOperatorCashier,
  setCashierTransactionAsResolved,
  startClosingCashier,
} from "../../api/cashier";

// Redux actions
import {
  setCashier,
  setCashierPaymentMethods,
  setShowNewCashierModal,
  validatePaymentMethods,
} from "../../store/actions/cashierSlice";

// Components
import TransactionListItem from "./transactions/TransactionListItem";
import WithdrawalDialog from "./transactions/WithdrawalDialog";
import ReinforcementDialog from "./transactions/ReinforcementDialog";
import PendentCashReturnDialog from "./transactions/PendentCashReturnDialog";
import PaymentMethodTransferDialog from "./transactions/PaymentMethodTransferDialog";
import CustomConfirmationDialog from "../dialogs/CustomConfirmationDialog";
import ShowCashier from "./ShowCashier";
import ClosingCashierView from "./ClosingCashierView";

// MUI Components
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

// Icons
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LockClockIcon from "@mui/icons-material/LockClock";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import RefreshIcon from "@mui/icons-material/Refresh";

// Utils
import { dispatchSnackBarMessage } from "../../utils/snackbar";
import { getUserOpenCashier } from "../../utils/cashier";
import { dateTimeMask } from "../../common/dateMask";
import store from "../../store/store";

// Animated Modal Components
import {
  AnimatedModal,
  ModalContent,
  ModalHeader,
} from "../modals/AnimatedModal";
import ConfirmModal from "../modals/confirmModal";
import { formatFinance } from "../../utils/formatters";

// Animation variants
const tabVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    y: 20,
    transition: {
      duration: 0.3,
      ease: "easeIn",
    },
  },
};

const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

// Skeleton loading component
const LoadingSkeleton = () => {
  const theme = useTheme();
  return (
    <Box sx={{ p: 3 }}>
      {/* Header skeleton */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" height={32} />
            <Skeleton variant="text" width="55%" height={20} sx={{ mt: 1 }} />
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Skeleton variant="rounded" width={100} height={24} />
              <Skeleton variant="rounded" width={140} height={24} />
              <Skeleton variant="rounded" width={120} height={24} />
            </Stack>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Skeleton variant="text" width={100} height={16} />
            <Skeleton variant="text" width={80} height={28} />
          </Box>
        </Stack>
      </Paper>

      {/* Action cards skeleton */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[0, 1, 2].map((i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Skeleton variant="rounded" height={80} />
          </Grid>
        ))}
      </Grid>

      {/* Button skeleton */}
      <Skeleton variant="rounded" width="100%" height={44} />
    </Box>
  );
};

export default function CashierInfoModal({ show, closeModal }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const cashier = useSelector((state) => state.cashier.cashier);
  const cashierPaymentMethods = useSelector(
    (state) => state.cashier.cashierPaymentMethods
  );

  const [showCloseCashierAlert, setShowCloseCashierAlert] = useState(false);
  const [isClosingCashier, setIsClosingCashier] = useState(false);

  const [showWithdrawalDialog, setShowWithdrawalDialog] = useState(false);
  const [showReinforcementDialog, setShowReinforcementDialog] = useState(false);
  const [showPendentCashReturnDialog, setShowPendentCashReturnDialog] =
    useState(false);
  const [showPaymentTypeTransferDialog, setShowPaymentTypeTransferDialog] =
    useState(false);
  const [paymentForTransferMethod, setPaymentForTransferMethod] =
    useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);

  const [loadingReport, setLoadingReport] = useState(true);

  const [page, setPage] = useState(0);
  const [loadingReportOrders, setLoadingReportOrders] = useState(true);

  const [reportOrders, setReportOrders] = useState([]);
  const [cashierTransactions, setCashierTransactions] = useState([]);
  const [transactionToResolve, setTransactionToResolve] = useState(null);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [showButtonMore, setShowButtonMore] = useState(true);

  const [isCashierClosed, setIsCashierClosed] = useState(false);
  const [showCashier, setShowCashier] = useState(false);
  const [showCashierActions, setShowCashierActions] = useState(null);

  const handleShowCashierActionsReady = useCallback((actions) => {
    setShowCashierActions(actions);
  }, []);

  const [dialogType, setDialogType] = useState(null);
  const [dialogContent, setDialogContent] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogConfirmText, setDialogConfirmText] = useState("");

  const [cashierPaymentMethodToDelete, setCashierPaymentMethodToDelete] =
    useState(null);

  // New state: tab navigation & live timer
  const [activeTab, setActiveTab] = useState(0);
  const [elapsedTime, setElapsedTime] = useState("");

  // Live timer - updates every minute
  useEffect(() => {
    if (!cashier?.date_time_begin || isCashierClosed || !show) return;

    const update = () => {
      const start = new Date(cashier.date_time_begin);
      const now = new Date();
      const diff = now - start;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      setElapsedTime(`${hours}h ${minutes}m`);
    };

    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [cashier?.date_time_begin, isCashierClosed, show]);

  useEffect(() => {
    if (show) {
      setActiveTab(0);
      setTimeout(async () => {
        await loadData(cashier?.id ?? undefined);
      }, 10);
    }
  }, [show]);

  const loadData = async (id) => {
    try {
      setLoadingReport(true);

      if (id && cashier.date_time_end) {
        setShowCashier(true);
      }

      const [cashierData] = await Promise.all([
        getOperatorCashier({ id }),
        loadCashierPaymentMethods(),
      ]);

      dispatch(setCashier(cashierData.data));

      setIsCashierClosed(!!cashierData?.data?.date_time_end);
      setIsClosingCashier(
        !!cashierData?.data?.started_finish_at &&
          !cashierData?.data?.date_time_end
      );

      await Promise.all([
        loadCashierTransactions(),
        loadReportUnfinishedOrders(),
      ]);
    } catch (e) {
      console.error(e);
      // Only a 404 means there is no open cashier; any other failure (network,
      // timeout, a side request) must not push the operator to open a new one.
      if (!id && e.response?.status === 404) {
        dispatch(setShowNewCashierModal(true));
        closeModal();
      } else {
        dispatchSnackBarMessage(
          `cashier-load-data-error-${new Date()}`,
          "Erro ao carregar dados do caixa",
          "error"
        );
      }
    } finally {
      setLoadingReport(false);
      setLoadingReportOrders(false);
    }
  };

  const loadCashierPaymentMethods = async () => {
    try {
      const params = { id: cashier.id };
      const { data } = await getCashierReportOrdersPaymentMethods(params);

      const existingMethods = [...cashierPaymentMethods];

      const mergedMethods = data.map((newMethod) => {
        const existingMethod = existingMethods.find(
          (method) => method.payment_method === newMethod.payment_method
        );

        return existingMethod
          ? { ...newMethod, value: existingMethod.value || null }
          : newMethod;
      });

      dispatch(setCashierPaymentMethods([...mergedMethods]));
    } catch (e) {
      console.log(JSON.stringify(e));
      if (cashier?.id) {
        dispatchSnackBarMessage(
          "cashier-load-transactions-error",
          "Erro ao carregar histórico de transações do caixa",
          "error"
        );
      }
    } finally {
      setLoadingReportOrders(false);
    }
  };

  const deleteCashierPaymentMethods = async () => {
    try {
      await deleteCashierPaymentMethodTransfer(cashierPaymentMethodToDelete);
      await loadCashierPaymentMethods();
    } catch (e) {
      if (cashier?.id) {
        dispatchSnackBarMessage(
          "cashier-load-transactions-error",
          "Erro ao deletar transferência de método de pagamento",
          "error"
        );
      }
    } finally {
      setLoadingReportOrders(false);
    }
  };

  const loadCashierTransactions = async () => {
    try {
      const { data } = await getCashierTransactions(cashier.id);

      setCashierTransactions(data);
    } catch (e) {
      if (cashier?.id) {
        dispatchSnackBarMessage(
          "cashier-load-transactions-error",
          "Erro ao carregar histórico de transações do caixa",
          "error"
        );
      }
    } finally {
      setLoadingReportOrders(false);
    }
  };

  const loadReportUnfinishedOrders = async (newSearch = true) => {
    try {
      setLoadingReportOrders(true);

      let pageNum = newSearch ? 1 : page + 1;
      const params = {
        id: cashier?.id,
        page: pageNum,
        pending: true,
      };
      const {
        data: { orders },
      } = await getCashierReportUnfinishedOrders(params);

      setReportOrders(orders);

      setPage(page + 1);

      let result;
      if (newSearch) {
        result = [...orders];
        switchShowButtonMore(orders.length);
      } else {
        const new_orders = orders.filter(
          ({ order_id: id1 }) =>
            !orders.some(({ order_id: id2 }) => id2 === id1)
        );
        result = [...orders, ...new_orders];
        switchShowButtonMore(new_orders.length);
      }

      setReportOrders(result);
    } catch (e) {
      if (cashier?.id) {
        dispatchSnackBarMessage(
          "cashier-orders-error",
          "Erro ao carregar pedidos abertos do caixa",
          "error"
        );
      }
    } finally {
      setLoadingReportOrders(false);
    }
  };

  const handleIsClosingCashier = async () => {
    try {
      if (isClosingCashier) {
        await cancelClosingCashier(cashier.id);
      }

      if (!isClosingCashier) {
        await startClosingCashier(cashier.id);
      }

      setIsClosingCashier(!isClosingCashier);

      await getUserOpenCashier();
    } catch (e) {
      console.error(e);
      dispatchSnackBarMessage(
        "cashier-handle-is-closing-error",
        "Erro ao atualizar caixa",
        "error"
      );
    }
  };

  const handleCloseCashier = () => {
    setShowCloseCashierAlert(!showCloseCashierAlert);
  };

  const handleCashierTransaction = async ({
    amount,
    description,
    transactionType,
    onClose,
  }) => {
    try {
      const body = {
        cashier: cashier.id,
        transaction_type: transactionType,
        amount_cents: amount,
        description: description,
      };

      await createCashierTransaction(cashier.id, body);
      await loadCashierTransactions();
      onClose();
    } catch (e) {
      const errorType =
        transactionType === 1
          ? "cashier-reinforcement-error"
          : transactionType === 3
          ? "cash-pendent-error"
          : "cashier-withdrawal-error";

      const errorMessage =
        transactionType === 1
          ? "Erro ao cadastrar reforço de caixa"
          : transactionType === 3
          ? "Erro ao cadastrar pendência de caixa"
          : "Erro ao cadastrar retirada";

      dispatchSnackBarMessage(errorType, errorMessage, "error");
    }
  };

  const handleCashierPaymentMethodTransfer = async ({
    amount_cents,
    from_payment_method,
    to_payment_method,
    onClose,
  }) => {
    try {
      const body = {
        cashier: cashier.id,
        amount_cents,
        from_payment_method,
        to_payment_method,
      };

      await createCashierPaymentMethodTransfer(body);
      await loadCashierPaymentMethods();
      onClose();
    } catch (e) {
      dispatchSnackBarMessage(
        "cashier-payment-transfer-error",
        "Erro ao criar transferência de método de pagamento",
        "error"
      );
    }
  };

  const handleShowDialog = (itemId = null, type = null) => {
    switch (type) {
      case "resolvePendentPayment":
        setTransactionToResolve(itemId);
        setDialogType(type);
        setDialogContent("Deseja marcar a pendência como resolvida?");
        setDialogTitle("Resolver Pendência?");
        setDialogConfirmText("Resolvido");
        break;

      case "paymentMethodTransfer":
        setCashierPaymentMethodToDelete(itemId);
        setDialogType(type);
        setDialogContent(
          "Deseja deletar essa transferência de métodos de pagamento?"
        );
        setDialogTitle("Deletar transferência entre métodos?");
        setDialogConfirmText("Deletar");
        break;

      case "deletePendentPayment":
        setTransactionToDelete(itemId);
        setDialogType(type);
        setDialogContent("Deseja deletar essa movimentação?");
        setDialogTitle("Deletar movimentação?");
        setDialogConfirmText("Deletar");
        break;

      default:
        break;
    }

    setDialogOpen(!dialogOpen);
  };

  const setTransactionAsResolved = async () => {
    try {
      await setCashierTransactionAsResolved(cashier.id, transactionToResolve);

      await loadData(cashier.id);
    } catch (e) {
      if (cashier?.id) {
        dispatchSnackBarMessage(
          "cashier-load-transactions-error",
          "Erro ao carregar histórico de transações do caixa",
          "error"
        );
      }
    } finally {
      setLoadingReportOrders(false);
    }
  };

  const deleteTransaction = async () => {
    try {
      const params = {
        transaction_id: transactionToDelete,
      };
      await deleteCashierTransaction(cashier.id, params);

      await loadData(cashier.id);
    } catch (e) {
      if (cashier?.id) {
        dispatchSnackBarMessage(
          "cashier-load-transactions-error",
          "Erro ao carregar histórico de transações do caixa",
          "error"
        );
      }
    } finally {
      setLoadingReportOrders(false);
    }
  };

  const handlePaymentTypeTransferDialog = (payment = null) => {
    setPaymentForTransferMethod(payment);
    setShowPaymentTypeTransferDialog(!showPaymentTypeTransferDialog);
  };

  const closeCashier = async () => {
    setLoadingReport(true);
    handleCloseCashier();
    dispatch(validatePaymentMethods());

    const updatedMethods = store.getState().cashier.cashierPaymentMethods;
    const isValid = !updatedMethods.some((method) => method.error);

    if (!isValid) {
      dispatchSnackBarMessage(
        "cashier-validation-error",
        "Preencha todos os campos de pagamento.",
        "error"
      );
      setLoadingReport(false);
      return;
    }

    try {
      await endCashier({
        id: cashier.id,
        paymentMethods: updatedMethods,
      });
      setIsCashierClosed(true);
      closeModal();
    } catch (e) {
      dispatchSnackBarMessage(
        "cashier-close-error",
        e.response.data
          ? `${e.response.data.error} ${
              e.response.data.orders
                ? `Pedidos: ${e.response.data.orders.join(", ")}`
                : ""
            }`
          : "Erro ao fechar o caixa.",
        "error"
      );
    } finally {
      setLoadingReport(false);
      handleCloseCashier();
      const foundCashier = await getUserOpenCashier();
      if (foundCashier === false) {
        dispatch(setShowNewCashierModal(true));
      }
    }
  };

  const switchShowButtonMore = (length) => {
    length > 0 ? setShowButtonMore(true) : setShowButtonMore(false);
  };

  const handleConfirmDialogSubmit = async () => {
    try {
      switch (dialogType) {
        case "resolvePendentPayment":
          return await setTransactionAsResolved();

        case "deletePendentPayment":
          return await deleteTransaction();

        case "paymentMethodTransfer":
          return await deleteCashierPaymentMethods();

        default:
          return () => {};
      }
    } catch (e) {
      console.error(e);
      dispatchSnackBarMessage(
        `${dialogTitle}-error`,
        `Erro ao ${dialogTitle}`,
        "error"
      );
    }
  };

  // Get modal width based on closing state and screen size
  const getModalWidth = () => {
    if (isMobile) return "95%";
    if (isTablet) return "90%";
    return isClosingCashier ? "90%" : "60%";
  };

  const isLoading = loadingReport || loadingReportOrders;
  const isActiveCashier = !isClosingCashier && !isCashierClosed && !showCashier;

  // Resolve the header title & icon based on current state
  const getHeaderConfig = () => {
    if (showCashier && !isLoading) {
      return {
        title: "Relatório de Caixa",
        icon: ReceiptLongIcon,
        subtitle: cashier
          ? `#${cashier.id} - ${dateTimeMask(cashier.date_time_begin)}`
          : null,
      };
    }
    if (isClosingCashier && !isLoading) {
      return {
        title: "Fechamento de Caixa",
        icon: LockClockIcon,
        subtitle: null,
      };
    }
    return {
      title: "Informações do Caixa",
      icon: PointOfSaleIcon,
      subtitle: null,
    };
  };

  const headerConfig = getHeaderConfig();

  // Build unified header content (left side)
  const headerCustomContent = (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.main,
          borderRadius: 1.5,
          p: 0.75,
          width: 36,
          height: 36,
        }}
      >
        <headerConfig.icon fontSize="small" />
      </Box>
      <Box>
        <Typography variant="h6" fontWeight={600} lineHeight={1.3}>
          {headerConfig.title}
        </Typography>
        {headerConfig.subtitle && (
          <Typography variant="caption" color="text.secondary">
            {headerConfig.subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );

  // Right-side header buttons for closed cashier report
  const headerRightContent =
    showCashier && !isLoading && showCashierActions ? (
      <Stack direction="row" spacing={1} sx={{ mr: 1 }}>
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
          <Tooltip title="Atualizar dados">
            <IconButton
              onClick={() => showCashierActions.loadReportInfos()}
              color="primary"
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </motion.div>
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<LocalPrintshopIcon />}
            onClick={() => showCashierActions.handlePrint()}
            disabled={
              !showCashierActions.isDataLoaded ||
              showCashierActions.printingStatus === "printing" ||
              showCashierActions.printingStatus === "preparing"
            }
            sx={{
              position: "relative",
              overflow: "hidden",
            }}
          >
            {showCashierActions.printingStatus === "printing" ||
            showCashierActions.printingStatus === "preparing" ? (
              <>
                <CircularProgress
                  size={20}
                  sx={{
                    color: "white",
                    position: "absolute",
                    left: "50%",
                    marginLeft: "-10px",
                  }}
                />
                <span style={{ opacity: 0 }}>Imprimir</span>
              </>
            ) : (
              "Imprimir"
            )}
          </Button>
        </motion.div>
      </Stack>
    ) : null;

  // Quick action card data
  const quickActions = [
    {
      label: "Reforço",
      subtitle: "Adicionar dinheiro",
      icon: TrendingUpIcon,
      color: theme.palette.success.main,
      onClick: () => setShowReinforcementDialog(true),
    },
    {
      label: "Retirada",
      subtitle: "Retirar dinheiro",
      icon: TrendingDownIcon,
      color: theme.palette.warning.main,
      onClick: () => setShowWithdrawalDialog(true),
    },
    {
      label: "Pendência",
      subtitle: "Registrar pendência",
      icon: AssignmentIcon,
      color: theme.palette.info.main,
      onClick: () => setShowPendentCashReturnDialog(true),
    },
  ];

  // --- Render: Active Cashier Status Header ---
  const renderStatusHeader = () => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.7),
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.05
        )} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>
            Caixa Ativo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Iniciado em {dateTimeMask(cashier?.date_time_begin)}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
            <Chip
              icon={<PointOfSaleIcon />}
              label="Aberto"
              color="success"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 500 }}
            />
            <Chip
              icon={<EventNoteIcon />}
              label={`${cashier?.first_name || ""} ${cashier?.last_name || ""}`.trim() || "Operador"}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 500 }}
            />
            {cashierTransactions.length > 0 && (
              <Chip
                icon={<SwapHorizIcon />}
                label={`${cashierTransactions.length} movimentação${cashierTransactions.length !== 1 ? "ões" : ""}`}
                variant="outlined"
                size="small"
                sx={{ fontWeight: 500 }}
              />
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            textAlign: { xs: "left", sm: "right" },
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "flex-start", sm: "flex-end" },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <AccessTimeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" color="text.secondary">
              Tempo de operação
            </Typography>
          </Stack>
          <Typography variant="h6" fontWeight={600} color="primary">
            {elapsedTime || "--"}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  // --- Render: Quick Action Cards (horizontal row) ---
  const renderQuickActions = () => (
    <Box>
      <Grid container spacing={2}>
        {quickActions.map((action) => (
          <Grid item xs={12} sm={4} key={action.label}>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(action.color, 0.2)}`,
                  bgcolor: alpha(action.color, 0.04),
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: alpha(action.color, 0.08),
                  },
                }}
                onClick={action.onClick}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: "50%",
                      bgcolor: alpha(action.color, 0.15),
                      display: "flex",
                    }}
                  >
                    <action.icon sx={{ color: action.color, fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {action.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {action.subtitle}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Iniciar Fechamento - separated, destructive action */}
      <Button
        variant="outlined"
        color="error"
        fullWidth
        startIcon={<LockClockIcon />}
        onClick={handleIsClosingCashier}
        sx={{
          mt: 3,
          py: 1.25,
          fontWeight: 600,
          borderStyle: "dashed",
          "&:hover": {
            borderStyle: "solid",
            bgcolor: alpha(theme.palette.error.main, 0.04),
          },
        }}
      >
        Iniciar Fechamento do Caixa
      </Button>
    </Box>
  );

  // --- Render: Resumo Tab ---
  const renderResumoTab = () => (
    <motion.div variants={tabVariants} initial="hidden" animate="visible" exit="exit">
      <Stack spacing={3}>
        {renderStatusHeader()}
        {renderQuickActions()}
      </Stack>
    </motion.div>
  );

  // --- Render: Movimentacoes Tab ---
  const renderMovimentacoesTab = () => (
    <motion.div variants={tabVariants} initial="hidden" animate="visible" exit="exit">
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          bgcolor: alpha(theme.palette.background.paper, 0.7),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <EventNoteIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Histórico de Movimentações
          </Typography>
          {cashierTransactions.length > 0 && (
            <Chip
              label={cashierTransactions.length}
              size="small"
              color="primary"
              sx={{ fontWeight: 600, minWidth: 28 }}
            />
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />

        {cashierTransactions.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                p: 2,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                mb: 2,
              }}
            >
              <ReceiptLongIcon
                sx={{ fontSize: 36, color: theme.palette.primary.main }}
              />
            </Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Nenhuma movimentação ainda
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 260 }}
            >
              Use as ações na aba Resumo para registrar reforços, retiradas ou pendências
            </Typography>
          </Box>
        ) : (
          <AnimatePresence>
            <motion.div variants={listVariants} initial="hidden" animate="visible">
              <List sx={{ p: 0 }}>
                {cashierTransactions.map((transaction) => (
                  <TransactionListItem
                    key={transaction.id}
                    transaction={transaction}
                    formatFinance={formatFinance}
                    dateTimeMask={dateTimeMask}
                    remove={() =>
                      handleShowDialog(transaction.id, "deletePendentPayment")
                    }
                    setAsResolved={() =>
                      handleShowDialog(transaction.id, "resolvePendentPayment")
                    }
                  />
                ))}
              </List>
            </motion.div>
          </AnimatePresence>
        )}
      </Paper>
    </motion.div>
  );

  return (
    <AnimatedModal
      isOpen={show}
      onClose={closeModal}
      width={getModalWidth()}
      maxWidth={isClosingCashier ? "1400px" : "1000px"}
    >
      {/* Single unified header - adapts to current cashier state */}
      <ModalHeader
        onClose={closeModal}
        customContent={headerCustomContent}
        rightContent={headerRightContent}
      />

      <ModalContent padding={0}>
        <Box sx={{ p: 0 }}>
          {/* Loading Skeleton */}
          {isLoading && <LoadingSkeleton />}

          {/* Closing Cashier View */}
          {isClosingCashier && !isLoading && (
            <ClosingCashierView
              reportOrders={reportOrders}
              setReportOrders={setReportOrders}
              cashierTransactions={cashierTransactions}
              cashierPaymentMethods={cashierPaymentMethods}
              showButtonMore={showButtonMore}
              loadReportUnfinishedOrders={loadReportUnfinishedOrders}
              handleShowDialog={handleShowDialog}
              handlePaymentTypeTransferDialog={handlePaymentTypeTransferDialog}
              handleIsClosingCashier={handleIsClosingCashier}
              handleCloseCashier={handleCloseCashier}
            />
          )}

          {/* Active Cashier View - Tab-based navigation */}
          {isActiveCashier && !isLoading && (
            <Box>
              {/* Tab bar */}
              <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, newVal) => setActiveTab(newVal)}
                  sx={{
                    minHeight: 44,
                    "& .MuiTab-root": {
                      minHeight: 44,
                      py: 1,
                    },
                  }}
                >
                  <Tab
                    label="Resumo"
                    icon={<PointOfSaleIcon sx={{ fontSize: 18 }} />}
                    iconPosition="start"
                    sx={{ gap: 0.5 }}
                  />
                  <Tab
                    label={
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <span>Movimentações</span>
                        {cashierTransactions.length > 0 && (
                          <Chip
                            label={cashierTransactions.length}
                            size="small"
                            color="primary"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        )}
                      </Stack>
                    }
                    icon={<EventNoteIcon sx={{ fontSize: 18 }} />}
                    iconPosition="start"
                    sx={{ gap: 0.5 }}
                  />
                </Tabs>
              </Box>

              {/* Tab content */}
              <Box sx={{ p: 3 }}>
                <AnimatePresence mode="wait">
                  {activeTab === 0 && (
                    <Box key="resumo">{renderResumoTab()}</Box>
                  )}
                  {activeTab === 1 && (
                    <Box key="movimentacoes">{renderMovimentacoesTab()}</Box>
                  )}
                </AnimatePresence>
              </Box>
            </Box>
          )}

          {/* Show full cashier report when it's closed - no extra header */}
          {showCashier && !isLoading && (
            <Box sx={{ px: 3, pb: 3 }}>
              <ShowCashier
                cashier={cashier}
                show={showCashier}
                hideHeader
                onActionsReady={handleShowCashierActionsReady}
              />
            </Box>
          )}
        </Box>
      </ModalContent>

      {/* All the dialog components */}
      <WithdrawalDialog
        open={showWithdrawalDialog}
        onClose={() => setShowWithdrawalDialog(false)}
        onConfirm={handleCashierTransaction}
      />

      <ReinforcementDialog
        open={showReinforcementDialog}
        onClose={() => setShowReinforcementDialog(false)}
        onConfirm={handleCashierTransaction}
      />

      <PendentCashReturnDialog
        open={showPendentCashReturnDialog}
        onClose={() => setShowPendentCashReturnDialog(false)}
        onConfirm={handleCashierTransaction}
      />

      <PaymentMethodTransferDialog
        open={showPaymentTypeTransferDialog}
        payment={paymentForTransferMethod}
        onClose={() => setShowPaymentTypeTransferDialog(false)}
        onConfirm={handleCashierPaymentMethodTransfer}
      />

      <ConfirmModal
        show={showCloseCashierAlert}
        label={"FECHAR CAIXA?"}
        message={"Tem certeza que deseja fechar o seu caixa atual?"}
        closeModal={handleCloseCashier}
        backBtnLabel={"Não Fechar"}
        confirmBtnLabel={"Fechar Caixa"}
        action={closeCashier}
      />

      <CustomConfirmationDialog
        onClose={handleShowDialog}
        dialogContent={dialogContent}
        title={dialogTitle}
        open={dialogOpen}
        onConfirm={handleConfirmDialogSubmit}
        confirmText={dialogConfirmText}
      />
    </AnimatedModal>
  );
}
